import React, { useEffect, useState, useRef, useImperativeHandle } from "react";
import { Texture, Mesh, Vector3, HemisphericLight, MeshBuilder, ArcRotateCamera, AssetsManager, StandardMaterial, Color3, Color4, DynamicTexture, Engine, RenderTargetTexture, VertexBuffer, Animation, CubicEase, EasingFunction, GreasedLineMesh, Camera, CreateGreasedLine } from "@babylonjs/core";
import SceneComponent from "./SceneComponent";
import "@babylonjs/loaders/glTF";
import { icons } from "../../icons";


const getParentSize = parent => {
    const sizes = parent.getHierarchyBoundingVectors()
    const size = {
        x: sizes.max.x - sizes.min.x,
        y: sizes.max.y - sizes.min.y,
        z: sizes.max.z - sizes.min.z
    }
    return size
}

const DesignerView = ({ modelUrl, canvasState, viewMode, selectedScene, selectedTool, envelopeDimension, settings, setModelLoaded, isRotating, setIsRotating }, ref) => {

    const engineOptions = React.useMemo(() => ({ useLogarithmicDepthBuffer: true }), []);

    const canvasRef = useRef(null);
    var sceneRef = useRef(null)
    const currentModel = useRef(null);
    const assetsManagerRef = useRef();
    const inactivityTimer = useRef(null);
    const isSpinningRef = useRef(false);
    const [iEnvelopeGrid, setIEnvelopeGrid] = useState([]);
    const lastApplyStateRef = useRef(null);
    const lastRedrawStateRef = useRef(null);
    const chunkAnimationFrameRef = useRef(null);

    const [sceneReady, setSceneReady] = useState(false);
    const [iEnvelope, setIEnvelope] = useState(null);
    const [iParavent, setIParavent] = useState(null);
    const [iScoop, setIScoop] = useState(null);
    const [iTop, setITop] = useState(null);

    useImperativeHandle(ref, () => ({
        downloadPictures: async (fileName = "file", selectedViews = [0]) => {
            takePictures(fileName, selectedViews, true);
        },
        getPictures: (selectedViews = [0]) => {
            return takePictures("temp", selectedViews, false)
        }
    }))

    const animateCamera = (camera, targetAlpha, targetBeta, targetRadius, targetTarget, onAnimationEnd) => {
        const frameRate = 60;
        const duration = 1;
        const totalFrames = frameRate * duration;

        const ease = new CubicEase();
        ease.setEasingMode(EasingFunction.EASINGMODE_EASEINOUT);

        // Normalize current alpha to be close to targetAlpha
        let currentAlpha = camera.alpha % (2 * Math.PI);
        if (currentAlpha < 0) currentAlpha += 2 * Math.PI;

        // Find shortest path
        let diff = targetAlpha - currentAlpha;
        if (diff > Math.PI) diff -= 2 * Math.PI;
        else if (diff < -Math.PI) diff += 2 * Math.PI;

        const startAlpha = targetAlpha - diff;

        const animAlpha = new Animation("animAlpha", "alpha", frameRate, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
        animAlpha.setKeys([{ frame: 0, value: startAlpha }, { frame: totalFrames, value: targetAlpha }]);
        animAlpha.setEasingFunction(ease);

        const animBeta = new Animation("animBeta", "beta", frameRate, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
        animBeta.setKeys([{ frame: 0, value: camera.beta }, { frame: totalFrames, value: targetBeta }]);
        animBeta.setEasingFunction(ease);

        const animRadius = new Animation("animRadius", "radius", frameRate, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
        animRadius.setKeys([{ frame: 0, value: camera.radius }, { frame: totalFrames, value: targetRadius }]);
        animRadius.setEasingFunction(ease);

        const animTarget = new Animation("animTarget", "target", frameRate, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CONSTANT);
        animTarget.setKeys([{ frame: 0, value: camera.target }, { frame: totalFrames, value: targetTarget }]);
        animTarget.setEasingFunction(ease);

        sceneRef.current.beginDirectAnimation(camera, [animAlpha, animBeta, animRadius, animTarget], 0, totalFrames, false, 1.0, onAnimationEnd);
    };

    const getCoordinatesFromId = ((id) => {
        const parts = id.split('-');
        if (parts.length === 2) {
            const rowStr = parts[0].startsWith('V') ? parts[0].slice(1) : parts[0];
            const colStr = parts[1].startsWith('V') ? parts[1].slice(1) : parts[1];
            return { row: parseInt(rowStr), col: parseInt(colStr) - 1 };
        }
        return null;
    });

    const parseColor = (color) => {
        if (!color || typeof color !== 'string') return Color3.Black();
        const hex = color.startsWith('#') ? color : `#${color}`;
        try {
            return Color3.FromHexString(hex);
        } catch (e) {
            console.warn("DEBUG: Failed to parse color:", color, e);
            return Color3.Black();
        }
    };

    const takePictures = (fileName, selectedViews, download = true, directoryHandle = null) => {
        if (sceneRef.current && currentModel.current) {
            const originalY = currentModel.current.position.y;
            currentModel.current.position.y = 0;
            currentModel.current.computeWorldMatrix(true);

            const boundingVectors = currentModel.current.getHierarchyBoundingVectors();
            const modelSize = {
                x: boundingVectors.max.x - boundingVectors.min.x,
                y: boundingVectors.max.y - boundingVectors.min.y,
                z: boundingVectors.max.z - boundingVectors.min.z
            };
            const modelCenter = boundingVectors.max.add(boundingVectors.min).scale(0.5);


            const width = download ? 1190 : 1684;
            const height = 1684;
            const aspect = width / height;
            const fov = 0.8; // Babylon default ArcRotateCamera FOV

            const envelopeRadius = Math.max(modelSize.x, modelSize.z) / 2;
            const envelopeCenterY = boundingVectors.max.y - envelopeRadius;

            const visualCenterY = (envelopeCenterY * 0.7) + (modelCenter.y * 0.3);
            const visualCenter = new Vector3(modelCenter.x, visualCenterY, modelCenter.z);

            const distFromTargetToTop = boundingVectors.max.y - visualCenter.y;
            const distFromTargetToBottom = visualCenter.y - boundingVectors.min.y;
            const maxVerticalSpanFromTarget = Math.max(distFromTargetToTop, distFromTargetToBottom);

            const getFitDistance = (w, h) => {
                const dV = (h / 2) / Math.tan(fov / 2);
                const dH = (w / 2) / (aspect * Math.tan(fov / 2));
                return Math.max(dV, dH) * 1.05; // 5% safe padding
            };

            const distSide = Math.max(
                getFitDistance(modelSize.x, maxVerticalSpanFromTarget * 2),
                getFitDistance(modelSize.z, maxVerticalSpanFromTarget * 2)
            );

            // The distance from the camera to the equator needed to fit the width/depth
            const distTopBase = getFitDistance(modelSize.x, modelSize.z) * 1.15; // Extra 15% padding specifically for top views to prevent crown clipping
            const distBottomBase = getFitDistance(modelSize.x, modelSize.z); // Standard padding for the bottom view to make it appear larger

            const cameraPositions = [
                { position: sceneRef.current.activeCamera.position.clone(), target: sceneRef.current.activeCamera.target.clone(), rotation: sceneRef.current.activeCamera.rotation.clone(), name: 'active-camera' },
                { position: visualCenter.add(new Vector3(0, 0, distSide)), target: visualCenter.clone(), rotation: new Vector3(0, 0, 0), name: 'back' },
                { position: visualCenter.add(new Vector3(0, 0, -distSide)), target: visualCenter.clone(), rotation: new Vector3(0, Math.PI, 0), name: 'front' },
                { position: visualCenter.add(new Vector3(distSide, 0, 0)), target: visualCenter.clone(), rotation: new Vector3(0, Math.PI / 2, 0), name: 'left' },
                { position: visualCenter.add(new Vector3(-distSide, 0, 0)), target: visualCenter.clone(), rotation: new Vector3(0, -Math.PI / 2, 0), name: 'right' },
                { position: new Vector3(visualCenter.x, envelopeCenterY + distTopBase, visualCenter.z), target: new Vector3(visualCenter.x, envelopeCenterY, visualCenter.z), rotation: new Vector3(Math.PI / 2, 0, 0), name: 'top' },
                { position: new Vector3(visualCenter.x, envelopeCenterY - distBottomBase, visualCenter.z), target: new Vector3(visualCenter.x, envelopeCenterY, visualCenter.z), rotation: new Vector3(-Math.PI / 2, 0, 0), name: 'bottom' },
                { position: visualCenter.add(new Vector3(-distSide * 0.65, -distSide * 0.65, distSide * 0.75)), target: visualCenter.clone(), rotation: new Vector3(-0.5, -0.7, 0), name: 'new-back' },
            ];

            currentModel.current.position.y = originalY;
            currentModel.current.computeWorldMatrix(true);
            const filteredCameraPositions = cameraPositions.filter((position, index) => {
                return selectedViews.includes(index);
            });

            const tempCamera = new ArcRotateCamera("TempCamera", 0, 0, 1, Vector3.Zero(), sceneRef.current);
            tempCamera.maxZ = 2000;
            tempCamera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;

            if (sceneRef.current.activeCamera) {
                sceneRef.current.activeCamera.fovMode = Camera.FOVMODE_VERTICAL_FIXED;
            }

            const rtt = new RenderTargetTexture("tempRtt", {
                width: width,
                height: height,
                samples: 4,
            }, sceneRef.current, false, false);
            rtt.renderList.push(...currentModel.current.getChildMeshes(), sceneRef.current.getMeshByName("skyBox"));

            const imagePromises = filteredCameraPositions.map((position, index) => {
                const isActiveView = position.name === 'active-camera';

                // If NOT active view, remove skybox for transparency
                if (!isActiveView) {
                    const skyboxMesh = sceneRef.current.getMeshByName("skyBox");
                    const skyboxIndex = rtt.renderList.indexOf(skyboxMesh);
                    if (skyboxIndex > -1) {
                        rtt.renderList.splice(skyboxIndex, 1);
                    }
                }

                return new Promise(resolve => {
                    const curentPosition = position;
                    tempCamera.position = curentPosition.position;
                    tempCamera.target = curentPosition.target;
                    tempCamera.rotation = curentPosition.rotation;

                    rtt.activeCamera = tempCamera;
                    rtt.render();

                    rtt.readPixels().then(data => {
                        const canvas = document.createElement('canvas');
                        canvas.width = width;
                        canvas.height = height;
                        const context = canvas.getContext('2d');

                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = width;
                        tempCanvas.height = height;
                        const tempContext = tempCanvas.getContext('2d');

                        const imageData = tempContext.createImageData(width, height);
                        imageData.data.set(data);
                        tempContext.putImageData(imageData, 0, 0);

                        context.translate(0, canvas.height);
                        context.scale(1, -1);
                        context.drawImage(tempCanvas, 0, 0);

                        // Restore skybox if it was removed
                        if (!isActiveView) {
                            rtt.renderList.push(sceneRef.current.getMeshByName("skyBox"));
                        }

                        const format = isActiveView ? "image/jpeg" : "image/png";
                        const extension = isActiveView ? "jpeg" : "png";
                        resolve({ src: canvas.toDataURL(format, 1), name: position.name, extension: extension });
                    });
                })

            });

            const picturesPromise = Promise.all(imagePromises)

            if (download) {
                const loadWatermarkPromise = new Promise(resolve => {

                    const watermarkImage = new Image();

                    watermarkImage.crossOrigin = "anonymous";
                    watermarkImage.src = settings.overlayUrl;

                    watermarkImage.src = settings.overlayUrl;
                    watermarkImage.onload = () => {
                        resolve(watermarkImage);
                    };
                });

                loadWatermarkPromise.then(watermarkImage => {
                    picturesPromise.then(imagesToReturn => {

                        const watermarkPromises = imagesToReturn.map(image => {
                            return new Promise(resolve => {
                                const canvas = document.createElement('canvas');
                                canvas.width = watermarkImage.width;
                                canvas.height = watermarkImage.height;
                                const context = canvas.getContext('2d');

                                const img = new Image();
                                img.onload = () => {
                                    context.drawImage(img, 0, 0, canvas.width, canvas.height);
                                    context.drawImage(watermarkImage, 0, 0);
                                    // Use the original format/extension
                                    const format = image.extension === 'png' ? "image/png" : "image/jpeg";
                                    resolve({ src: canvas.toDataURL(format, 1), name: image.name, extension: image.extension });
                                };
                                img.src = image.src;
                            });
                        });

                        Promise.all(watermarkPromises).then(watermarkedImages => {
                            downloadPictures(fileName, watermarkedImages, directoryHandle);
                            tempCamera.dispose();
                        });
                    });
                })
            } else {
                return picturesPromise.then(imagesToReturn => {
                    return imagesToReturn;
                }).finally(() => {
                    tempCamera.dispose();
                });
            }
        }
    };

    const downloadPictures = async (fileName, pictures, directoryHandle = null) => {
        const downloadPicture = async (picture) => {
            if (directoryHandle) {
                try {
                    const fileHandle = await directoryHandle.getFileHandle(`${fileName}_${picture.name}.jpeg`, { create: true });
                    const writable = await fileHandle.createWritable();
                    const response = await fetch(picture.src);
                    const blob = await response.blob();
                    await writable.write(blob);
                    await writable.close();

                } catch (error) {
                    console.error('Error during download:', error);
                }
            } else {

                const link = document.createElement('a');
                link.href = picture.src;
                const extension = picture.extension || 'jpeg';
                link.download = `${fileName}_${picture.name}.${extension}`;
                link.click();

            }
        };

        await Promise.all(pictures.map(downloadPicture));
    };

    const applyCanvasState = (state) => {
        if (!state) return;

        // Skip if the grid state hasn't changed (deep compare simplified with stringify check)
        // We only compare the grid here; redrawCanvas handles its own change detection
        const gridKey = JSON.stringify(state.envelopeGrid);
        if (lastApplyStateRef.current === gridKey) {
            // Even if grid hasn't changed, we might need a texture refresh if coming from hidden
            redrawCanvas(state.images, state.texts, state.paraventLogo);
            return;
        }
        lastApplyStateRef.current = gridKey;

        // Cancel any pending chunking
        if (chunkAnimationFrameRef.current) {
            cancelAnimationFrame(chunkAnimationFrameRef.current);
        }

        const grid = state.envelopeGrid;
        const totalRows = grid.length;
        let currentRow = 0;

        const processChunks = () => {
            const startTime = performance.now();
            // Process as many rows as possible in ~8ms
            while (currentRow < totalRows && performance.now() - startTime < 8) {
                const row = grid[currentRow];
                if (currentRow === totalRows - 1) {
                    if (iTop && iTop.material) iTop.material.diffuseColor = Color3.FromHexString(row[0]);
                } else {
                    if (currentRow === 0 && iScoop && iScoop.material) {
                        iScoop.material.diffuseColor = Color3.FromHexString(row[0]);
                    }
                    if (iEnvelopeGrid[currentRow]) {
                        row.forEach((colorHex, x) => {
                            const mesh = iEnvelopeGrid[currentRow][x];
                            if (mesh && mesh.material) mesh.material.diffuseColor = Color3.FromHexString(colorHex);
                        });
                    }
                }
                currentRow++;
            }

            if (currentRow < totalRows) {
                chunkAnimationFrameRef.current = requestAnimationFrame(processChunks);
            } else {
                // Finalize textures after grid is done color-coding
                if (iEnvelope && iEnvelope.material && iEnvelope.material.opacityTexture) {
                    const texture = iEnvelope.material.opacityTexture;
                    texture.clear();
                    if (iParavent && iParavent.material && iParavent.material.opacityTexture) {
                        const paraventTexture = iParavent.material.opacityTexture;
                        paraventTexture.clear();

                        redrawCanvas(state.images, state.texts, state.paraventLogo, true);
                        paraventTexture.update();
                        iParavent.material.needsUpdate = true;
                    }
                    texture.update();
                    iEnvelope.material.needsUpdate = true;
                }
                chunkAnimationFrameRef.current = null;
            }
        };

        chunkAnimationFrameRef.current = requestAnimationFrame(processChunks);
    }

    const redrawCanvas = (images, texts, paraventLogo, force = false) => {
        // Change detection to avoid expensive 4K texture redrawing
        const stateKey = JSON.stringify({ images, texts, paraventLogo });
        if (lastRedrawStateRef.current === stateKey && !force) return;
        lastRedrawStateRef.current = stateKey;


        if (iEnvelope) {
            const texture = iEnvelope.material.opacityTexture;
            const context = texture.getContext();
            context.canvas.width = context.canvas.width; // Reset context state (clears clip region and canvas)
            const widthConversionScale = 4096 / envelopeDimension.width;
            const heightConversionScale = 4096 / (envelopeDimension.paraventRadius * 2);

            // Clip the context to the printable area (excluding Nomex rows)
            context.beginPath();
            context.rect(0, envelopeDimension.top * heightConversionScale, 4096, envelopeDimension.height * heightConversionScale);
            context.clip();

            const imageLoadPromises = (images || []).map(image => {
                return new Promise((resolve) => {
                    const img = new Image();
                    let src = image.src;

                    if (src && src.toLowerCase().startsWith('data:image/svg')) {
                        try {
                            const isBase64 = src.includes(';base64,');
                            const dataStart = src.indexOf(',') + 1;
                            const header = src.substring(0, dataStart);
                            const data = src.substring(dataStart);

                            let svgString = isBase64 ? atob(data) : decodeURIComponent(data);

                            const parser = new DOMParser();
                            const doc = parser.parseFromString(svgString, "image/svg+xml");
                            const svgElement = doc.querySelector("svg");

                            if (svgElement) {
                                svgElement.setAttribute("preserveAspectRatio", "none");
                                const serializer = new XMLSerializer();
                                const newSvgString = serializer.serializeToString(doc);
                                src = header + (isBase64 ? btoa(newSvgString) : encodeURIComponent(newSvgString));
                            }
                        } catch (error) {
                            console.warn("Error patching SVG for Firefox compatibility:", error);
                        }
                    }

                    img.src = src;
                    img.onload = () => resolve(img);
                    img.onerror = () => {
                        console.error("Error loading image:", image.src);
                        resolve(null);
                    };
                });
            });

            Promise.all(imageLoadPromises)
                .then(loadedImages => {
                    loadedImages.forEach((img, index) => {
                        if (!img) return;
                        const image = images[index];
                        context.save();
                        context.translate(image.centerPointX * widthConversionScale, image.centerPointY * heightConversionScale);
                        context.scale(widthConversionScale, heightConversionScale);
                        context.rotate(image.angle * Math.PI / 180);
                        context.scale(image.scaleX, image.scaleY);
                        const drawWidth = image.width || img.width;
                        const drawHeight = image.height || img.height;
                        context.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
                        context.restore();
                    });

                    if (texts && texts.length > 0) {
                        texts.forEach((text) => {
                            const fontString = `${text.fontStyle} ${text.fontWeight} ${text.fontSize}px ${text.fontFamily}`;

                            context.font = fontString;
                            context.fillStyle = text.fill;
                            context.textAlign = "center";
                            context.textBaseline = "middle";

                            context.save();
                            context.translate(text.centerPointX * widthConversionScale, text.centerPointY * heightConversionScale);
                            context.scale(widthConversionScale, heightConversionScale);
                            context.rotate(text.angle * Math.PI / 180);
                            context.scale(text.scaleX, text.scaleY);

                            const lines = text.text.split('\n');
                            const totalHeight = lines.length * text.lineHeight * text.fontSize;
                            lines.forEach((line, lineIndex) => {
                                // Offset each line relative to the vertical middle of the multi-line block
                                const yOffset = (lineIndex - (lines.length - 1) / 2) * text.lineHeight * text.fontSize;
                                context.fillText(line, 0, yOffset);
                            });

                            context.restore();
                        });
                    }

                    texture.update();
                })
                .catch(error => {
                    console.error("Error loading images:", error);
                });
        }
        if (iParavent) {
            var paraventTexture = iParavent.material.opacityTexture;
            var paraventContext = paraventTexture.getContext();
            paraventContext.clearRect(0, 0, 1024, 1024); // Clear paravent context
            var widthConversionScaleParavent = 1024 / (envelopeDimension.paraventRadius * 2);
            var heightConversionScaleParavent = 1024 / (envelopeDimension.paraventRadius * 2);
            if (paraventLogo) {
                var paraventLogoLeft = paraventLogo.centerPointX - (envelopeDimension.width / 2 - envelopeDimension.paraventRadius);
                var paraventLogoTop = paraventLogo.centerPointY;
                const img = new Image();
                img.src = paraventLogo.src;
                img.onload = () => {
                    paraventContext.save();
                    paraventContext.translate(paraventLogoLeft * widthConversionScaleParavent, paraventLogoTop * heightConversionScaleParavent);
                    paraventContext.scale(widthConversionScaleParavent, heightConversionScaleParavent);
                    paraventContext.rotate(paraventLogo.angle * Math.PI / 180 + Math.PI);
                    paraventContext.scale(paraventLogo.scaleX, paraventLogo.scaleY);
                    paraventContext.drawImage(img, -img.width / 2, -img.height / 2, img.width, img.height);
                    paraventContext.restore();
                    paraventTexture.update();
                }
            } else {
                paraventTexture.update(); // Update even if empty to show clear state
            }
        }
    };


    useEffect(() => {
        const hasGrid = canvasState && !!canvasState.envelopeGrid;
        const isReady = sceneReady && !!iScoop && !!iTop && iEnvelopeGrid.length > 0;
        const validMode = viewMode !== 'Grid';

        if (hasGrid && isReady && validMode) {
            applyCanvasState(canvasState)
        }

    }, [canvasState, iScoop, iEnvelopeGrid, iTop, sceneReady, viewMode, envelopeDimension]);


    useEffect(() => {
        if (sceneRef.current && currentModel.current) {
            const loadlineMeshes = currentModel.current.getDescendants(false, (node) =>
                node.name && node.name.startsWith("loadline-")
            );

            const color = canvasState.loadlines;
            const isVisible = !!color;
            const babylonColor = isVisible ? parseColor(color) : null;

            loadlineMeshes.forEach(mesh => {
                mesh.setEnabled(isVisible);
                if (babylonColor) {
                    mesh.color = babylonColor;
                    if (mesh.material) {
                        mesh.material.disableLighting = true;
                        mesh.material.emissiveColor = babylonColor;
                    }
                }
            });
        }
    }, [canvasState.loadlines]);


    useEffect(() => {
        if (sceneReady && modelUrl) {
            var material = new StandardMaterial("modelMaterial", sceneRef.current);
            material.diffuseColor = new Color3(0.5, 0.5, 0.5);
            material.specularColor = new Color3(0.1, 0.1, 0.1);
            material.backFaceCulling = false;
            material.zOffset = 4;

            var texture = new DynamicTexture("envelope dynamic texture", 4096, sceneRef.current, true, Texture.TRILINEAR_SAMPLINGMODE);
            texture.wrapU = Texture.WRAP_ADDRESSMODE;
            texture.uOffset = 0.5;
            texture.anisotropicFilteringLevel = sceneRef.current.getEngine().getCaps().maxAnisotropy;

            var envelopeMaterial = new StandardMaterial("envelope material", sceneRef.current);
            envelopeMaterial.opacityTexture = texture;
            envelopeMaterial.diffuseTexture = texture;
            envelopeMaterial.diffuseTexture.hasAlpha = true;
            envelopeMaterial.specularColor = new Color3(0.1, 0.1, 0.1); // Subtle shine
            envelopeMaterial.alphaMode = Engine.ALPHA_COMBINE;
            envelopeMaterial.hasAlpha = true;
            envelopeMaterial.backFaceCulling = true; // Enable backface culling
            envelopeMaterial.zOffset = 0;

            var textureParavent = new DynamicTexture("paravent dynamic texture", 1024, sceneRef.current, true);
            var paraventMaterial = new StandardMaterial("paravent material", sceneRef.current);
            paraventMaterial.opacityTexture = textureParavent;
            paraventMaterial.diffuseTexture = textureParavent;
            paraventMaterial.diffuseTexture.hasAlpha = true;
            paraventMaterial.specularColor = new Color3(0.1, 0.1, 0.1);
            paraventMaterial.alphaMode = Engine.ALPHA_COMBINE;
            paraventMaterial.hasAlpha = true;
            paraventMaterial.zOffset = 0;

            const labelPanelMaterial = new StandardMaterial("labelPanelMaterial", sceneRef.current);
            const labelTexture = new Texture(icons.labelPng, sceneRef.current);
            labelTexture.wrapU = Texture.CLAMP_ADDRESSMODE;
            labelTexture.wrapV = Texture.CLAMP_ADDRESSMODE;


            labelPanelMaterial.diffuseTexture = labelTexture;

            labelPanelMaterial.diffuseColor = new Color3(1, 1, 1);
            labelPanelMaterial.specularColor = new Color3(0.1, 0.1, 0.1);
            labelPanelMaterial.backFaceCulling = false;
            labelPanelMaterial.zOffset = 2;

            if (modelUrl) {
                let modelPath = `${modelUrl.substring(0, modelUrl.lastIndexOf('/') + 1)}`
                let modelName = modelUrl.substring(modelUrl.lastIndexOf('/') + 1);

                const balloonTask = assetsManagerRef.current.addMeshTask("balloonTask", "", modelPath, modelName);
                balloonTask.onSuccess = (task) => {
                    if (!sceneRef.current || sceneRef.current.isDisposed) return;
                    const modelElementsArray = [];
                    currentModel.current = new Mesh("BalloonModel", sceneRef.current);
                    const allMeshesByColumn = {};

                    task.loadedMeshes.forEach((mesh) => {
                        mesh.setParent(currentModel.current);
                        let clonedMaterial = material.clone();
                        const match = getCoordinatesFromId(mesh.name);
                        if (match) {

                            while (modelElementsArray.length <= match.row) {
                                modelElementsArray.push([]);
                            }
                            while (modelElementsArray[match.row].length <= match.col) {
                                modelElementsArray[match.row].push(null)
                            }
                            mesh.material = clonedMaterial;
                            mesh.material.alpha = 1;
                            modelElementsArray[match.row][match.col] = mesh;

                            if (!allMeshesByColumn[match.col]) allMeshesByColumn[match.col] = [];
                            allMeshesByColumn[match.col].push(mesh);

                            if (mesh.name === '00-1') {
                                mesh.computeWorldMatrix(true);
                                const boundingBox = mesh.getBoundingInfo().boundingBox;
                                const center = boundingBox.centerWorld;
                                const size = boundingBox.maximumWorld.subtract(boundingBox.minimumWorld);

                                const normal = center.clone().normalize();

                                const labelHeight = size.y / 6;
                                const aspectRatio = 210 / 300;
                                const labelWidth = labelHeight * aspectRatio;
                                const labelDepth = labelWidth;

                                const decalSize = new Vector3(labelWidth, labelHeight, labelDepth);

                                const decal = MeshBuilder.CreateDecal("label-sticker", mesh, {
                                    position: center,
                                    normal: normal,
                                    size: decalSize,
                                    angle: 0
                                });

                                decal.material = labelPanelMaterial;

                                decal.material = labelPanelMaterial;
                                decal.material.zOffset = -2;

                                decal.setParent(currentModel.current);
                            }
                        } else if (mesh.name === "KOLECKO") {
                            mesh.material = clonedMaterial
                            mesh.material.alpha = 1;
                            setITop(mesh);
                        } else if (mesh.name === "OBAL") {
                            mesh.material = envelopeMaterial;
                            mesh.material.alpha = 1;
                            mesh.scaling.scaleInPlace(1.001); // Scale up slightly to prevent Z-fighting without offsets
                            mesh.alphaIndex = 2;
                            setIEnvelope(mesh)
                        } else if (mesh.name === "VENTIL") {
                            mesh.material = paraventMaterial;
                            mesh.material.alpha = 1;
                            mesh.scaling.scaleInPlace(1.001);
                            mesh.alphaIndex = 2;
                            setIParavent(mesh)
                        } else if (mesh.name === "SCOOP") {
                            mesh.material = clonedMaterial;
                            mesh.material.alpha = 1;
                            setIScoop(mesh)
                        }

                    });
                    // --- SEAM DETECTION PARITY WITH index.html ---
                    // 1. Calculate center and prepare parameters BEFORE scaling the model
                    currentModel.current.computeWorldMatrix(true);
                    const initialBoundingVectors = currentModel.current.getHierarchyBoundingVectors();
                    const modelCenter = initialBoundingVectors.max.add(initialBoundingVectors.min).scale(0.5);
                    const colIndices = Object.keys(allMeshesByColumn).map(Number).sort((a, b) => a - b);
                    const proximityEps = 0.0005;
                    const babylonLoadlineColor = parseColor(canvasState.loadlines || "#FFFFFF");

                    const getGlobalVertices = (meshes) => {
                        const points = [];
                        meshes.forEach(m => {
                            const pos = m.getVerticesData(VertexBuffer.PositionKind);
                            if (!pos) return;
                            const wm = m.getWorldMatrix();
                            for (let i = 0; i < pos.length; i += 3) {
                                points.push(Vector3.TransformCoordinates(new Vector3(pos[i], pos[i + 1], pos[i + 2]), wm));
                            }
                        });
                        return points;
                    };

                    colIndices.forEach((colA, i) => {
                        const colB = colIndices[(i + 1) % colIndices.length];
                        const pointsA = getGlobalVertices(allMeshesByColumn[colA]);
                        const pointsB = getGlobalVertices(allMeshesByColumn[colB]);

                        const sharedPoints = [];
                        const bucketSize = 0.01;
                        const bucketsB = new Map();

                        // Fill buckets for pointsB
                        pointsB.forEach(p => {
                            const bx = Math.floor(p.x / bucketSize);
                            const by = Math.floor(p.y / bucketSize);
                            const bz = Math.floor(p.z / bucketSize);
                            const key = `${bx},${by},${bz}`;
                            if (!bucketsB.has(key)) bucketsB.set(key, []);
                            bucketsB.get(key).push(p);
                        });

                        // 3D Grid search (mathematically identical to nested loops but O(N))
                        for (let pA of pointsA) {
                            const bx = Math.floor(pA.x / bucketSize);
                            const by = Math.floor(pA.y / bucketSize);
                            const bz = Math.floor(pA.z / bucketSize);

                            let found = false;
                            // Check 3x3x3 neighborhood (27 buckets)
                            for (let dx = -1; dx <= 1; dx++) {
                                for (let dy = -1; dy <= 1; dy++) {
                                    for (let dz = -1; dz <= 1; dz++) {
                                        const key = `${bx + dx},${by + dy},${bz + dz}`;
                                        const bucket = bucketsB.get(key);
                                        if (bucket) {
                                            for (let pB of bucket) {
                                                if (Vector3.Distance(pA, pB) < proximityEps) {
                                                    found = true;
                                                    break;
                                                }
                                            }
                                        }
                                        if (found) break;
                                    }
                                    if (found) break;
                                }
                                if (found) break;
                            }

                            if (found) {
                                if (!sharedPoints.some(existing => Vector3.Distance(pA, existing) < 0.0001)) {
                                    sharedPoints.push(pA.clone());
                                }
                            }
                        }

                        if (sharedPoints.length > 1) {
                            sharedPoints.sort((a, b) => a.y - b.y);

                            const offsetPoints = sharedPoints.map(p => {
                                const dir = p.subtract(modelCenter).normalize();
                                return p.add(dir.scale(0.002));
                            });

                            const line = MeshBuilder.CreateLines(`loadline-${colA}-${colB}`, {
                                points: offsetPoints,
                                updatable: true,
                                instances: null
                            }, sceneRef.current);
                            line.alphaIndex = 1;

                            line.color = babylonLoadlineColor;
                            line.alpha = 0.5; // Make lines look "thinner" and more subtle
                            if (line.material) {
                                line.material.disableLighting = true;
                                line.material.emissiveColor = babylonLoadlineColor;
                                line.material.alpha = 0.5;
                                line.material.backFaceCulling = false;
                                line.material.zOffset = 2;
                            }
                            line.setParent(currentModel.current);
                        }
                    });

                    // --- Final Model Scaling ---
                    const modelScaling = 100 / getParentSize(currentModel.current).y
                    currentModel.current.scaling = new Vector3(modelScaling, modelScaling, modelScaling);
                    currentModel.current.rotation.y = Math.PI;
                    setIEnvelopeGrid(modelElementsArray);
                }
                balloonTask.onError = (task, message, exception) => {
                    console.error("Asset Task Error:", message, exception);
                };
                assetsManagerRef.current.useDefaultLoadingScreen = false;
                assetsManagerRef.current.onTasksDoneObservable.add(function (task) {
                    setModelLoaded(true);

                })
                assetsManagerRef.current.onTaskErrorObservable.add(function (task, message, exception) {
                    console.error(message, exception);
                });
                assetsManagerRef.current.load();
            }
        }

    }, [modelUrl, sceneReady])


    useEffect(() => {
        if (sceneReady && selectedScene) {
            const skyboxMesh = sceneRef.current.getMeshByName("skyBox");
            if (skyboxMesh) {
                const skyboxMaterial = skyboxMesh.material;
                if (skyboxMaterial.diffuseTexture)
                    skyboxMaterial.diffuseTexture.dispose();
                if (skyboxMaterial.emissiveTexture)
                    skyboxMaterial.emissiveTexture.dispose();
                if (selectedScene.background) {
                    try {
                        const texture = new Texture(
                            selectedScene.background,
                            sceneRef.current, true, false, Texture.TRILINEAR_SAMPLINGMODE);

                        skyboxMaterial.diffuseTexture = texture;
                        skyboxMaterial.diffuseTexture.coordinatesMode = Texture.FIXED_EQUIRECTANGULAR_MIRRORED_MODE;
                        skyboxMaterial.emissiveTexture = texture;
                        skyboxMaterial.emissiveTexture.coordinatesMode = Texture.FIXED_EQUIRECTANGULAR_MIRRORED_MODE;
                        skyboxMaterial.emissiveColor = new Color3(1, 1, 1);

                    } catch (error) {
                        skyboxMaterial.diffuseColor = Color3.FromHexString(selectedScene.color);
                        skyboxMaterial.diffuseTexture = null;
                        skyboxMaterial.emissiveColor = Color3.FromHexString(selectedScene.color);
                        skyboxMaterial.emissiveTexture = null;
                    }
                }
                else {
                    skyboxMaterial.diffuseColor = Color3.FromHexString(selectedScene.color);
                    skyboxMaterial.diffuseTexture = null;
                    skyboxMaterial.emissiveColor = Color3.FromHexString(selectedScene.color);
                    skyboxMaterial.emissiveTexture = null;
                }
            }
        }
    }, [selectedScene, sceneReady]);

    useEffect(() => {
        if (sceneReady && selectedTool) {
            const camera = sceneRef.current.activeCamera;
            if (camera) {
                if (selectedTool === "Paravent") {
                    animateCamera(camera, -Math.PI / 2, 0, 150, Vector3.Zero());
                } else if (["Graphic", "Text"].includes(selectedTool)) {
                    animateCamera(camera, -Math.PI / 2, Math.PI / 2, 150, Vector3.Zero());
                }
            }
        }
    }, [selectedTool, sceneReady]);

    useEffect(() => {
        setModelLoaded(false);
    }, [])

    useEffect(() => {
        const restartInactivityTimer = () => {
            if (inactivityTimer.current) {
                clearTimeout(inactivityTimer.current);
            }
            inactivityTimer.current = setTimeout(() => {
                setIsRotating(true);
            }, 30000);
        };

        if (isRotating) {
            isSpinningRef.current = false;
            if (sceneRef.current && sceneRef.current.activeCamera) {
                const camera = sceneRef.current.activeCamera;
                animateCamera(camera, camera.alpha, Math.PI / 2, 150, Vector3.Zero(), () => {
                    isSpinningRef.current = true;
                });
            }
        } else {
            isSpinningRef.current = false;
        }

        const handleInteraction = (event) => {
            if (event && event.type === 'pointerdown' && event.target.closest('#rotate-btn-toggle')) {
                return;
            }

            if (isRotating) {
                setIsRotating(false);
            }
            restartInactivityTimer();
        };

        window.addEventListener('pointerdown', handleInteraction);
        window.addEventListener('keydown', handleInteraction);
        window.addEventListener('wheel', handleInteraction);

        restartInactivityTimer();

        return () => {
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
            isSpinningRef.current = false;
            window.removeEventListener('pointerdown', handleInteraction);
            window.removeEventListener('keydown', handleInteraction);
            window.removeEventListener('wheel', handleInteraction);
        };
    }, [isRotating, setIsRotating]);

    useEffect(() => {
        return () => {
            if (chunkAnimationFrameRef.current) {
                cancelAnimationFrame(chunkAnimationFrameRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const preventDefault = (e) => {
            e.preventDefault();
        };

        const canvas = canvasRef.current;
        if (canvas && sceneReady) {
            canvas.addEventListener("wheel", preventDefault, { passive: false });
            canvas.addEventListener("gesturestart", preventDefault, { passive: false });
            canvas.addEventListener("gesturechange", preventDefault, { passive: false });
            canvas.addEventListener("gestureend", preventDefault, { passive: false });
        }

        return () => {
            if (canvas) {
                canvas.removeEventListener("wheel", preventDefault);
                canvas.removeEventListener("gesturestart", preventDefault);
                canvas.removeEventListener("gesturechange", preventDefault);
                canvas.removeEventListener("gestureend", preventDefault);
            }
        };
    }, [sceneReady]);

    const onSceneReady = (scene) => {

        scene.clearColor = new Color4(0, 0, 0, 0);
        const camera = new ArcRotateCamera("Camera", -Math.PI / 2, Math.PI / 2, 150, Vector3.Zero(), sceneRef.current);
        camera.maxZ = 2000;
        camera.setTarget(Vector3.Zero());
        camera.lowerRadiusLimit = 80;
        camera.upperRadiusLimit = 240;
        camera.pinchPrecision = 1;
        camera.wheelPrecision = 3;
        canvasRef.current = sceneRef.current.getEngine().getRenderingCanvas();
        camera.attachControl(canvasRef.current, true);
        const ambientLight = new HemisphericLight("ambientLight", new Vector3(1, 0, 0.3), sceneRef.current);
        ambientLight.groundColor = new Color3(0.9, 0.9, 0.9);
        ambientLight.intensity = 0.98;
        const engine = scene.getEngine();
        engine.setHardwareScalingLevel(0.5); // Render at 2x resolution for sharpness

        const skyboxMaterial = new StandardMaterial("skyBox", sceneRef.current);
        skyboxMaterial.backFaceCulling = false;
        skyboxMaterial.disableLighting = true;

        const skybox = MeshBuilder.CreateSphere("skyBox", { diameter: 800, segments: 32, sideOrientation: Mesh.BACKSIDE }, sceneRef.current);
        skybox.material = skyboxMaterial;

        assetsManagerRef.current = new AssetsManager(sceneRef.current);
        setSceneReady(true);
    };

    /**
     * Will run on every frame render.
     */
    const onRender = (scene) => {
        if (currentModel.current) {
            const time = performance.now() / 1000;
            const bounceHeight = 2.5;
            const bounceSpeed = 0.5;
            currentModel.current.position.y = Math.sin(time * bounceSpeed) * bounceHeight;
        }
        if (isRotating && isSpinningRef.current && sceneRef.current && sceneRef.current.activeCamera) {
            const camera = sceneRef.current.activeCamera;
            const rotationSpeed = (2 * Math.PI) / 120000;
            camera.alpha = (camera.alpha + rotationSpeed * scene.getEngine().getDeltaTime()) % (2 * Math.PI);
        }
    };
    return (
        <SceneComponent
            sceneRef={sceneRef}
            antialias={true}
            engineOptions={engineOptions}
            onSceneReady={onSceneReady}
            onRender={onRender}
            isVisible={viewMode !== 'Grid'}
            style={{ flex: 1, width: "100%", position: viewMode === 'Grid' ? 'absolute' : 'relative', left: viewMode === 'Grid' ? '-9999px' : '0', visibility: viewMode === 'Grid' ? 'hidden' : 'visible', overflow: "hidden", objectFit: "contain", touchAction: "none" }}
            id="designer-view" />
    )
};

export default React.forwardRef(DesignerView);